import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  acceptCreatorCollaborationAndCreateCampaign,
  evaluateCreatorCollaborationAdvisor,
  getCreatorCollaborationAdvisorStatus,
} from "../services/creatorAdvisorService";

const initialState = {
  evaluation: null,
  campaignResult: null,
  advisorStatus: null,

  evaluateLoading: false,
  acceptLoading: false,
  statusLoading: false,

  evaluateError: null,
  acceptError: null,
  statusError: null,

  lastRequestId: "",
};

const pickRequestId = (payload) =>
  payload?.requestId || payload?.request_id || "";

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;

  if (typeof error === "string") return error;

  if (error.message) return error.message;

  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  if (error.data?.message) {
    return error.data.message;
  }

  return fallback;
};

export const evaluateCreatorCollaborationAdvisorAsync = createAsyncThunk(
  "creatorAdvisor/evaluate",
  async (payload, { rejectWithValue }) => {
    try {
      return await evaluateCreatorCollaborationAdvisor(payload);
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Unable to evaluate creator collaboration.")
      );
    }
  }
);

export const getCreatorCollaborationAdvisorStatusAsync = createAsyncThunk(
  "creatorAdvisor/getStatus",
  async (requestId, { rejectWithValue }) => {
    try {
      return await getCreatorCollaborationAdvisorStatus(requestId);
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Unable to refresh creator advisor status.")
      );
    }
  }
);

export const acceptCreatorCollaborationAndCreateCampaignAsync = createAsyncThunk(
  "creatorAdvisor/acceptAndCreateCampaign",
  async ({ requestId, payload }, { rejectWithValue }) => {
    try {
      return await acceptCreatorCollaborationAndCreateCampaign(
        requestId,
        payload
      );
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Unable to create creator campaign.")
      );
    }
  }
);

const creatorAdvisorSlice = createSlice({
  name: "creatorAdvisor",
  initialState,
  reducers: {
    clearCreatorAdvisorErrors: (state) => {
      state.evaluateError = null;
      state.acceptError = null;
      state.statusError = null;
    },

    clearCreatorAdvisorCampaignResult: (state) => {
      state.campaignResult = null;
    },

    clearCreatorAdvisorStatus: (state) => {
      state.advisorStatus = null;
      state.statusError = null;
      state.statusLoading = false;
    },

    resetCreatorAdvisor: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(evaluateCreatorCollaborationAdvisorAsync.pending, (state) => {
        state.evaluateLoading = true;
        state.evaluateError = null;
        state.statusError = null;
        state.campaignResult = null;
        state.advisorStatus = null;
      })
      .addCase(
        evaluateCreatorCollaborationAdvisorAsync.fulfilled,
        (state, action) => {
          state.evaluateLoading = false;
          state.evaluation = action.payload || null;
          state.lastRequestId = pickRequestId(action.payload);
        }
      )
      .addCase(
        evaluateCreatorCollaborationAdvisorAsync.rejected,
        (state, action) => {
          state.evaluateLoading = false;
          state.evaluateError =
            action.payload || "Unable to evaluate creator collaboration.";
        }
      )

      .addCase(getCreatorCollaborationAdvisorStatusAsync.pending, (state) => {
        state.statusLoading = true;
        state.statusError = null;
      })
      .addCase(
        getCreatorCollaborationAdvisorStatusAsync.fulfilled,
        (state, action) => {
          state.statusLoading = false;
          state.advisorStatus = action.payload || null;

          const requestId = pickRequestId(action.payload);
          if (requestId) {
            state.lastRequestId = requestId;
          }
        }
      )
      .addCase(
        getCreatorCollaborationAdvisorStatusAsync.rejected,
        (state, action) => {
          state.statusLoading = false;
          state.statusError =
            action.payload || "Unable to refresh creator advisor status.";
        }
      )

      .addCase(
        acceptCreatorCollaborationAndCreateCampaignAsync.pending,
        (state) => {
          state.acceptLoading = true;
          state.acceptError = null;
        }
      )
      .addCase(
        acceptCreatorCollaborationAndCreateCampaignAsync.fulfilled,
        (state, action) => {
          state.acceptLoading = false;
          state.campaignResult = action.payload || null;

          const requestId = pickRequestId(action.payload);
          if (requestId) {
            state.lastRequestId = requestId;
          }
        }
      )
      .addCase(
        acceptCreatorCollaborationAndCreateCampaignAsync.rejected,
        (state, action) => {
          state.acceptLoading = false;
          state.acceptError =
            action.payload || "Unable to create creator campaign.";
        }
      );
  },
});

export const {
  clearCreatorAdvisorErrors,
  clearCreatorAdvisorCampaignResult,
  clearCreatorAdvisorStatus,
  resetCreatorAdvisor,
} = creatorAdvisorSlice.actions;

export const selectCreatorAdvisorEvaluation = (state) =>
  state.creatorAdvisor?.evaluation || null;

export const selectCreatorAdvisorCampaignResult = (state) =>
  state.creatorAdvisor?.campaignResult || null;

export const selectCreatorAdvisorStatus = (state) =>
  state.creatorAdvisor?.advisorStatus || null;

export const selectCreatorAdvisorEvaluateLoading = (state) =>
  Boolean(state.creatorAdvisor?.evaluateLoading);

export const selectCreatorAdvisorAcceptLoading = (state) =>
  Boolean(state.creatorAdvisor?.acceptLoading);

export const selectCreatorAdvisorStatusLoading = (state) =>
  Boolean(state.creatorAdvisor?.statusLoading);

export const selectCreatorAdvisorLoading = (state) =>
  Boolean(
    state.creatorAdvisor?.evaluateLoading ||
      state.creatorAdvisor?.acceptLoading ||
      state.creatorAdvisor?.statusLoading
  );

export const selectCreatorAdvisorEvaluateError = (state) =>
  state.creatorAdvisor?.evaluateError || null;

export const selectCreatorAdvisorAcceptError = (state) =>
  state.creatorAdvisor?.acceptError || null;

export const selectCreatorAdvisorStatusError = (state) =>
  state.creatorAdvisor?.statusError || null;

export const selectCreatorAdvisorErrors = (state) =>
  [
    state.creatorAdvisor?.evaluateError || null,
    state.creatorAdvisor?.acceptError || null,
    state.creatorAdvisor?.statusError || null,
  ].filter(Boolean);

export const selectCreatorAdvisorLastRequestId = (state) =>
  state.creatorAdvisor?.lastRequestId || "";

export default creatorAdvisorSlice.reducer;